import style from "@/styles/pages/AcceptTerms/AcceptTerms.module.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createApiClient } from "@/lib/Requests";
import { useEffect, useRef, useState } from "react";
import { RootState } from "@/store/dtm";
import clsx from "clsx";
import { set, useForm } from "react-hook-form";
import FadeTransition from "@/styles/animation/FadeTransition";
import { ApplicationDto, InitIdVerificationDto } from "@/lib/DTMWebAPIClient.generated";
import { setApplicationEndpointReached, setApplicationId, setApplicationProgress, setIdVerificationSubmitted, setWelcomeEmailSent} from "@/slices/applicationSlice";
import { Toast } from "primereact/toast";
import { generateSessionId, setClickInsightEvents } from "@/lib/util";
import { setAlloySessionId, setConsentCompleted, setIdVerificationStatus } from "@/slices/applicantSlice";
import { setAMLFieldsCompleted } from "@/slices/legalTermsSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { verificationCategory } from "@/types/applicationType";
import { setLegalComplete } from "@/slices/progressSlice";

export default function Attestation() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isTermsAccepted, setIsTermsAccepted] = useState(false);
    const [applicationStarted, setApplicationStarted] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const application = useSelector(
        (state: RootState) => state.application.application
    );
    const applicant = useSelector(
        (state: RootState) => state.applicant.applicant
    );
    const methods = useForm({
        defaultValues: {
            meridianCommunication: false,
        },
    });
    const credential = useSelector(
        (state: RootState) => state.credential
    );
    const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });
    const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
    const infoToast = useRef<Toast>(null);

    useEffect(() => {

        dispatch(setAMLFieldsCompleted(true));

        //Check if this existing member has an active deal
        client.apiDealsSearchMember().then(result => {
            if (result.length > 0) {
                //This condition exists to ensure a co-applicant (existing member) does not have an active deal before accepting the current one from the primary
                if (application.id == 0 && result[0] && result[0].id != application.id){
                    dispatch(setApplicationId(result[0].id ? result[0].id : 0));
                    dispatch(setApplicationProgress(7));
                    infoToast.current?.show({ severity: 'info', summary: 'According to our records, you started an application with us. Please click "Continue" to resume your progress.', life: 10000 });
                    setApplicationStarted(true);

                    //If a deal exists, check if it was submitted to Fundmore (if not resume progress to personal details)
                    if (result[0].status == "SubmittedForUnderwriting"){
                        dispatch(setConsentCompleted(true));
                        dispatch(setApplicationProgress(17));
                        dispatch(setApplicationEndpointReached(true));
                        dispatch(setLegalComplete(true));
                        dispatch(setIdVerificationStatus(verificationCategory.Pass));
                        dispatch(setIdVerificationSubmitted(true));
                        initializeAlloySession(result[0].id ? result[0].id : 0, true);
                    }else{
                        initializeAlloySession(result[0].id ? result[0].id : 0);
                    }
                }

                //After /amend is called, co-applicant will be part of the deal. This check ensures that alloy session is started with deal ID attached
                if (application.id != 0 && result[0] && result[0].id == application.id){
                    initializeAlloySession(application.id);
                }
            } else {
                initializeAlloySession(0);
            }
        }).catch((error) => {
            console.log("Error checking for existing deal");
        });
        setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);

    }, []);

    const onSubmit = async (data: any) => {
        if (!application.welcomeEmailSent){
            setIsSaving(true);
            await client.apiApplicantsWelcome(applicant.email).then(result => {
            dispatch(setWelcomeEmailSent(true));
            handleNavigation();
        }).catch((error) => {
            console.log("Error sending welcome email", error);
            handleNavigation();
        });
        }else{
            handleNavigation();
        }
    }

    const handleNavigation = async () => {
        let localInfo: any = {}
        localInfo.applicant = applicant;
        localInfo.application = application;
        setIsSaving(false);

        await client.apiApplicationsSave(new ApplicationDto({
            applicationData: JSON.stringify(localInfo)
        })).then(result => {
            if (application.id != 0 && applicationStarted) {
                navigate("/home", { replace: true });
            } else {
                if (isTermsAccepted) {
                    application.isCoApplicant ? navigate("/joint/mortgage-details", { replace: true })
                        : navigate("/single/select-application", { replace: true });
                }
            }
        }).catch((error) => {
            console.log("Error saving application info");
        });
    }


    const onError = (errors: object) => {
        console.log(errors);
    }

    const handleTermsToggle = (e: any) => {
        setIsTermsAccepted(e.target.checked);
    };

    const initializeAlloySession = async (dealId: number, completed?: boolean) => {
        let alloySessionId = applicant.alloySessionId;
        if (alloySessionId == "") {
            await client.apiIdverificationIdverificationapplicant().then(idVerification => {
                if (idVerification) {
                    alloySessionId = idVerification.alloySessionId;
                }
            }).catch((error) => {
                console.log(error);
            });

            if (alloySessionId == "") {
                alloySessionId = generateSessionId(application.isCoApplicant);

                client.apiIdverificationInit(new InitIdVerificationDto({
                    alloySessionId: alloySessionId,
                    applicationId: dealId,
                    applicantId: applicant.id,
                    status: completed ? "PASS" : "PENDING",
                    phoneVerified: true
                })).then(function (data) {

                }).catch((error) => {
                    console.log(error);
                });
            }
            dispatch(setAlloySessionId(alloySessionId));
        }
    }

    return (
        <FadeTransition>
            <Toast ref={infoToast} />
            <main aria-labelledby="main-content">
                <form onSubmit={methods.handleSubmit(onSubmit, onError)}>
                    <div className="main-container max-md:mt-10">
                        <div className="grid grid-cols-1 md:grid-cols-1 sm:hidden mt-10">
                            <div>
                                <img src="assets/acceptterms.svg" width="250" height="250" alt="accept-meridian-lending-terms"></img>
                            </div>
                        </div>
                        <div className={style.contentContainer}>
                            <div className="flex flex-col max-xs:px-3 max-md:items-center">
                                <div className={style.emHeading}>
                                    <div className="grid grid-cols-1">
                                        <h1>Welcome to Meridian Lending</h1>
                                        <p>
                                            Before we start we need to get your consent.
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-5 flex flex-col-reverse">
                                    <label></label>
                                    <input
                                        id="termsAccepted"
                                        type="checkbox"
                                        checked={isTermsAccepted}
                                        onChange={handleTermsToggle}
                                        className="accent"
                                    />
                                    <label htmlFor="termsAccepted">
                                        <p className="sub-heading ml-7 -mt-5">
                                            I have read and agree to Meridian Credit Union's <a className="active" style={{ textDecoration: "underline" }}
                                                target="_blank" href="https://www.meridiancu.ca/about-meridian/privacy-and-security?_gl=1*11lr0k1*_ga*MTA5MzU5NzM4NS4xNjg4MDQ2ODM4*_ga_6D78MN23LS*MTY4ODA0NjgzOC4xLjEuMTY4ODA0Njg3Ni4yMi4wLjA">privacy statement</a> for the Protection of Personal Information as it pertains to the collection and management of my personal information.
                                        </p>
                                    </label>
                                </div>
                            </div>


                            <div className="grid grid-cols-1 md:grid-cols-1 max-sm:hidden">
                                <div>
                                    <img src="assets/acceptterms.svg" width="250" height="250" alt="accept-meridian-lending-terms"></img>
                                </div>
                            </div>
                        </div>
                        <div className="mb-5">
                            <button
                                type="submit"
                                disabled={isSaving}
                                className={clsx(isTermsAccepted ? "" : "disabled", "primary mt-5 w-full h-11 px-6")}>
                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                {application.id != 0 ? "Continue" : "Get Started"}
                            </button>
                        </div>
                    </div>
                </form>
            </main>
        </FadeTransition>
    );
}